/**
 * Effect types for impact-path edges — the systems-thinking vocabulary from
 * specs/impact-path-v2-prd.md §3.
 *
 * Keys are English and ASCII because they are stored (v2: in
 * `pathway_connections.effect_type`; the impact map still uses `connection_type`)
 * and travel over the API; the Finnish
 * words the participant sees ("mahdollistaa", "estää") are i18n labels on top
 * of these keys, the same way every other three-language string works here.
 *
 * Copies: packages/shared/effectTypes.js (the display imports it) and
 * apps/backend/src/shared/ (Railway builds the backend from its own folder, so
 * it cannot import packages/). scripts/check-shared-copies.js keeps them
 * identical.
 *
 * `causal: false` is the point of the whole vocabulary: a `relates_to` or
 * `is_same` edge is excluded from reach, centrality and loop detection, so a
 * lateral association never inflates the structural maths.
 */

export const EFFECT_TYPES = {
  leads_to:    { polarity: '+', causal: true },                  // johtaa — default
  reinforces:  { polarity: '+', causal: true },                  // vahvistaa
  weakens:     { polarity: '-', causal: true },                  // heikentää
  // Not a matter of degree: A cancels B out. A workshop reaches for this when
  // one intervention undoes another, and saying "heikentää" understates it.
  nullifies:   { polarity: '-', causal: true },                  // kumoaa
  enables:     { polarity: 'enabling', causal: true },           // mahdollistaa
  blocks:      { polarity: '-', causal: true },                  // estää
  requires:    { polarity: 'enabling', causal: true },           // edellyttää
  delays:      { polarity: '+', causal: true, delayed: true },   // viivästyttää
  relates_to:  { polarity: '0', causal: false },                 // liittyy
  // Not a relation between two things at all: a claim that they are one thing
  // said twice. On a map several people build in parallel the same consequence
  // arrives in two branches with different words, and calling that "vahvistaa"
  // says the map contains a feedback that it does not.
  is_same:     { polarity: '0', causal: false },                 // sama asia
};

export const DEFAULT_EFFECT_TYPE = 'leads_to';

/**
 * Values written before the vocabulary existed. `multi-parent` is what the v1
 * endpoint hardcoded for every borrowed-parent link; the other three are the
 * types named in migration 025's column comment.
 */
export const LEGACY_EFFECT_TYPES = {
  'multi-parent': 'leads_to',
  consequence: 'leads_to',
  requirement: 'requires',
  related: 'relates_to',
};

export const EFFECT_TYPE_KEYS = Object.keys(EFFECT_TYPES);

/**
 * @param {unknown} value
 * @returns {string|null} canonical effect type, or null if unrecognised
 */
export function normalizeEffectType(value) {
  if (typeof value !== 'string') return null;
  const key = value.trim();
  if (!key) return null;
  if (EFFECT_TYPES[key]) return key;
  return LEGACY_EFFECT_TYPES[key] || null;
}

/**
 * Does this edge count as causal for structural analysis? Unknown values are
 * treated as causal — a stored type we don't recognise is more likely an older
 * causal link than a lateral one, and silently dropping edges from the maths
 * would be worse than including them.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isCausalEffect(value) {
  const key = normalizeEffectType(value);
  if (!key) return true;
  return EFFECT_TYPES[key].causal;
}

/**
 * @param {unknown} value
 * @returns {'+'|'-'|'enabling'|'0'|null}
 */
export function effectPolarity(value) {
  const key = normalizeEffectType(value);
  return key ? EFFECT_TYPES[key].polarity : null;
}
